import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target, TrendingUp, CheckCircle, Database, Activity, Lock, Zap, ArrowRight,
  Shield, AlertTriangle, FileCheck, Users, BarChart3, PieChart, LineChart
} from 'lucide-react';
import { 
  PieChart as RechartPie, Pie, Cell, ResponsiveContainer, 
  LineChart as RechartLine, Line, XAxis, YAxis, Tooltip, 
  BarChart, Bar, Legend 
} from 'recharts';

// Dashboard Card Component
const DashboardCard = ({ title, children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 ${className}`}
  >
    <h3 className="text-sm font-semibold text-gray-500 mb-4">{title}</h3>
    {children}
  </motion.div>
);

// Compliance Gauge Component
const ComplianceGauge = ({ value, label, color }) => (
  <div className="text-center">
    <div className="relative inline-flex items-center justify-center">
      <svg className="w-24 h-24 transform -rotate-90">
        <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="8" fill="none" />
        <circle 
          cx="48" cy="48" r="40" 
          stroke={color} 
          strokeWidth="8" 
          fill="none"
          strokeDasharray={`${value * 2.51} 251`}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute text-2xl font-bold text-[#1a3a52]">{value}%</span>
    </div>
    <p className="text-sm text-gray-600 mt-2">{label}</p>
  </div>
);

const SmartAuditPage = () => {
  const frameworks = [
    { name: 'ISO 27001', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { name: 'NIST CSF 2.0', color: 'bg-green-100 text-green-700 border-green-200' },
    { name: 'BIA', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    { name: 'DPIA', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { name: 'TPRM', color: 'bg-pink-100 text-pink-700 border-pink-200' },
    { name: 'Riscos', color: 'bg-red-100 text-red-700 border-red-200' },
    { name: 'SOX', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Visão Única de Riscos',
      description: 'Consolide todos os frameworks em uma única plataforma com scores automáticos de maturidade e risco.'
    },
    {
      icon: TrendingUp,
      title: 'Priorização de Ações',
      description: 'Dashboards executivos que orientam investimentos e priorizam tratamentos de risco.'
    },
    {
      icon: CheckCircle,
      title: 'Redução de Esforço',
      description: 'Evidências organizadas reduzem drasticamente o tempo de preparação para auditorias.'
    },
    {
      icon: Database,
      title: 'Evidências Organizadas',
      description: 'Repositório centralizado de documentos, políticas e evidências de conformidade.'
    }
  ];

  // Mock data for charts
  const riskData = [
    { name: 'Low', value: 20, color: '#22c55e' },
    { name: 'Medium', value: 20, color: '#f59e0b' },
    { name: 'High', value: 60, color: '#ef4444' },
  ];

  const vulnerabilityData = [
    { month: 'Set', critica: 70, alta: 30, media: 20, baixa: 10 },
    { month: 'Out', critica: 20, alta: 50, media: 30, baixa: 15 },
    { month: 'Nov', critica: 200, alta: 80, media: 40, baixa: 20 },
    { month: 'Dez', critica: 120, alta: 60, media: 35, baixa: 18 },
    { month: 'Jan', critica: 82, alta: 45, media: 28, baixa: 12 },
    { month: 'Fev', critica: 80, alta: 40, media: 25, baixa: 10 },
  ];

  const trainingData = [
    { name: 'TI', completed: 95, inProgress: 3, notStarted: 2 },
    { name: 'RH', completed: 88, inProgress: 8, notStarted: 4 },
    { name: 'Financeiro', completed: 75, inProgress: 15, notStarted: 10 },
    { name: 'Comercial', completed: 68, inProgress: 20, notStarted: 12 },
    { name: 'Jurídico', completed: 60, inProgress: 25, notStarted: 15 },
  ];

  return (
    <>
      <Helmet>
        <title>SmartAudit - DMM Security | Plataforma de Gestão de Riscos e Compliance</title>
        <meta name="description" content="SmartAudit: plataforma proprietária para mapear processos, consolidar frameworks e calcular automaticamente scores de risco e maturidade. ISO 27001, NIST CSF, PPSI 2.0." />
      </Helmet>

      <div className="min-h-screen pt-20" data-testid="smartaudit-page">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-[#0f2535] via-[#1a3a52] to-[#1e4d6b] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 50%, rgba(16,185,129,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59,130,246,0.3) 0%, transparent 50%)`
            }} />
          </div>
          
          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#10b981]/20 text-[#10b981] text-sm font-semibold rounded-full mb-6">
                <Zap className="w-4 h-4" />
                Plataforma Proprietária
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                SmartAudit
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Plataforma para gestão integrada de riscos, compliance e maturidade em segurança da informação
              </p>
            </motion.div>
          </div>
        </section>

        {/* What is SmartAudit */}
        <section className="py-20 bg-white" data-testid="what-is-section">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-block px-4 py-1.5 bg-[#10b981]/10 text-[#10b981] text-sm font-semibold rounded-full mb-4">
                  O que é
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-6">
                  Inteligência em Gestão de Riscos
                </h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                  <p>
                    O <strong className="text-[#1a3a52]">SmartAudit</strong> é nossa plataforma proprietária desenvolvida para 
                    <strong className="text-[#10b981]"> mapear processos</strong>, consolidar múltiplos frameworks de segurança e 
                    compliance, e <strong className="text-[#10b981]">calcular automaticamente</strong> scores de risco e níveis de maturidade.
                  </p>
                  <p>
                    A plataforma permite que lideranças de segurança, compliance e GRC tenham 
                    <strong className="text-[#1a3a52]"> visibilidade em tempo real</strong> do status de conformidade, riscos críticos 
                    e evolução da maturidade, através de <strong className="text-[#1a3a52]">dashboards executivos</strong> e 
                    <strong className="text-[#1a3a52]"> KPIs objetivos</strong>.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Frameworks Integration */}
        <section className="py-20 bg-gray-50" data-testid="frameworks-section">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <span className="inline-block px-4 py-1.5 bg-[#1a3a52]/10 text-[#1a3a52] text-sm font-semibold rounded-full mb-4">
                  Frameworks Integrados
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                  Convergência para o PPSI 2.0
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Múltiplos frameworks consolidados em um único hub central para governança unificada
                </p>
              </motion.div>

              {/* Integration Flow Visual */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="flex flex-col items-center gap-6">
                  {/* Modules */}
                  <div className="w-full">
                    <p className="text-sm font-semibold text-gray-500 mb-4 text-center">Módulos de Origem</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                      {frameworks.map((fw, idx) => (
                        <motion.div
                          key={fw.name}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.05 }}
                          className={`${fw.color} rounded-lg p-3 text-center text-sm font-semibold border`}
                        >
                          {fw.name}
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex flex-col items-center">
                    <div className="w-0.5 h-12 bg-gradient-to-b from-gray-200 to-[#10b981]" />
                    <span className="text-xs text-gray-400 my-2">Sincronização</span>
                    <ArrowRight className="w-5 h-5 text-[#10b981] rotate-90" />
                  </div>

                  {/* PPSI Hub */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-[#1a3a52] to-[#10b981] rounded-2xl p-8 text-center text-white shadow-xl shadow-[#10b981]/20 max-w-sm w-full"
                  >
                    <Lock className="w-12 h-12 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">PPSI 2.0</h3>
                    <p className="text-emerald-100">Hub Central • 25 Controles</p>
                    <p className="text-sm text-emerald-200 mt-2">25/25 mapeados</p>
                  </motion.div>

                  {/* Arrow */}
                  <div className="flex flex-col items-center">
                    <ArrowRight className="w-5 h-5 text-[#10b981] rotate-90" />
                    <span className="text-xs text-gray-400 my-2">Consolidação</span>
                    <div className="w-0.5 h-12 bg-gradient-to-b from-[#10b981] to-gray-200" />
                  </div>

                  {/* Results */}
                  <div className="w-full">
                    <p className="text-sm font-semibold text-gray-500 mb-4 text-center">Resultados</p>
                    <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                      {[
                        { name: 'Dashboard', color: 'bg-green-100 text-green-700 border-green-200' },
                        { name: 'Relatórios', color: 'bg-purple-100 text-purple-700 border-purple-200' },
                        { name: 'KPIs', color: 'bg-orange-100 text-orange-700 border-orange-200' },
                      ].map((result, idx) => (
                        <motion.div
                          key={result.name}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.1 }}
                          className={`${result.color} rounded-lg p-4 text-center font-semibold border`}
                        >
                          {result.name}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dashboard Demo Section */}
        <section className="py-20 bg-white" data-testid="dashboard-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="inline-block px-4 py-1.5 bg-[#10b981]/10 text-[#10b981] text-sm font-semibold rounded-full mb-4">
                Visão Executiva
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                Information Security Compliance Dashboard
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                ISO 27001 & PPSI 2.0 Overview
              </p>
            </motion.div>

            {/* Dashboard Header */}
            <div className="max-w-6xl mx-auto">
              <div className="bg-[#1a3a52] rounded-t-2xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Activity className="w-5 h-5 text-[#10b981]" />
                  <span className="text-sm font-medium">16 KPIs manuais integrados</span>
                </div>
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-white/10 rounded-lg text-white text-sm font-medium">ISO 27001</span>
                  <span className="px-3 py-1 bg-white/10 rounded-lg text-white text-sm font-medium">PPSI 2.0</span>
                </div>
              </div>

              {/* Dashboard Grid */}
              <div className="bg-gray-50 rounded-b-2xl p-6 border border-gray-200 border-t-0">
                {/* Top Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <DashboardCard title="Compliance Status: ISO 27001">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-8 h-8 text-[#10b981]" />
                      <div>
                        <p className="text-3xl font-bold text-[#10b981]">24.2%</p>
                        <p className="text-sm text-gray-500">Compliant</p>
                      </div>
                    </div>
                  </DashboardCard>

                  <DashboardCard title="Compliance Status: PPSI 2.0">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-8 h-8 text-[#10b981]" />
                      <div>
                        <p className="text-3xl font-bold text-[#10b981]">32%</p>
                        <p className="text-sm text-gray-500">Compliant</p>
                      </div>
                    </div>
                  </DashboardCard>

                  <DashboardCard title="Open Security Incidents">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-8 h-8 text-red-500" />
                      <div>
                        <p className="text-3xl font-bold text-red-500">0</p>
                        <p className="text-sm text-gray-500">High Risk</p>
                      </div>
                    </div>
                  </DashboardCard>

                  <DashboardCard title="Audit Findings">
                    <div className="flex items-center gap-3">
                      <FileCheck className="w-8 h-8 text-[#1a3a52]" />
                      <div>
                        <p className="text-3xl font-bold text-[#1a3a52]">0</p>
                        <p className="text-sm text-gray-500">Open Issues</p>
                      </div>
                    </div>
                  </DashboardCard>
                </div>

                {/* Charts Row */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Risk Assessment */}
                  <DashboardCard title="Risk Assessment" className="md:col-span-1">
                    <div className="flex items-center gap-6">
                      <div className="w-32 h-32">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartPie data={riskData} innerRadius={35} outerRadius={50}>
                            {riskData.map((entry, index) => (
                              <Pie
                                key={`cell-${index}`}
                                data={[entry]}
                                dataKey="value"
                                cx="50%"
                                cy="50%"
                                innerRadius={35}
                                outerRadius={50}
                              >
                                <Cell fill={entry.color} />
                              </Pie>
                            ))}
                          </RechartPie>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-2">
                        {riskData.map((item) => (
                          <div key={item.name} className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                            <span className="text-sm text-gray-600">{item.name}</span>
                            <span className="text-sm font-bold text-[#1a3a52]">{item.name === 'High' ? '3' : '1'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-4 flex items-center gap-1">
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Automático</span>
                      Dados do módulo de Riscos
                    </p>
                  </DashboardCard>

                  {/* Training Completion */}
                  <DashboardCard title="Training Completion">
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trainingData} layout="vertical">
                          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={60} />
                          <Tooltip />
                          <Bar dataKey="completed" stackId="a" fill="#3b82f6" name="Completed" />
                          <Bar dataKey="inProgress" stackId="a" fill="#f59e0b" name="In Progress" />
                          <Bar dataKey="notStarted" stackId="a" fill="#ef4444" name="Not Started" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex gap-4 mt-4">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Phishing:</p>
                        <p className="text-lg font-bold text-blue-600">91%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Data Protection:</p>
                        <p className="text-lg font-bold text-orange-500">82%</p>
                      </div>
                    </div>
                  </DashboardCard>
                </div>

                {/* Vulnerability & Compliance */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Vulnerability Management */}
                  <DashboardCard title="Vulnerability Management">
                    <p className="text-xs text-gray-400 mb-4">Open Vulnerabilities • Last 6 Months</p>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartLine data={vulnerabilityData}>
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="critica" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} name="Crítica" />
                          <Line type="monotone" dataKey="alta" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} name="Alta" />
                          <Line type="monotone" dataKey="media" stroke="#eab308" strokeWidth={2} dot={{ r: 4 }} name="Média" />
                          <Line type="monotone" dataKey="baixa" stroke="#22c55e" strokeWidth={2} dot={{ r: 4 }} name="Baixa" />
                        </RechartLine>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex gap-6 mt-4">
                      <div>
                        <p className="text-xs text-gray-500">Patches Applied</p>
                        <p className="text-lg font-bold text-red-500">36</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Avg. Remediation Time</p>
                        <p className="text-lg font-bold text-orange-500">7.2 Days</p>
                      </div>
                    </div>
                  </DashboardCard>

                  {/* Compliance Overview */}
                  <DashboardCard title="Compliance Overview">
                    <div className="flex flex-col items-center">
                      <div className="relative w-40 h-40">
                        {/* Gauge Background */}
                        <svg className="w-full h-full" viewBox="0 0 100 60">
                          <path
                            d="M 10 50 A 40 40 0 0 1 90 50"
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="8"
                            strokeLinecap="round"
                          />
                          {/* Colored segments */}
                          <path
                            d="M 10 50 A 40 40 0 0 1 30 15"
                            fill="none"
                            stroke="#ef4444"
                            strokeWidth="8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M 30 15 A 40 40 0 0 1 50 10"
                            fill="none"
                            stroke="#f59e0b"
                            strokeWidth="8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M 50 10 A 40 40 0 0 1 90 50"
                            fill="none"
                            stroke="#22c55e"
                            strokeWidth="8"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-end justify-center pb-2">
                          <span className="text-3xl font-bold text-red-500">27%</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">Overall Compliance</p>
                      <div className="flex gap-6 mt-4">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Internal Audits</p>
                          <p className="text-lg font-bold text-[#1a3a52]">3 <span className="text-xs font-normal">Conducted</span></p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Policy Updates</p>
                          <p className="text-lg font-bold text-[#1a3a52]">5 <span className="text-xs font-normal">Reviewed</span></p>
                        </div>
                      </div>
                    </div>
                  </DashboardCard>
                </div>

                {/* Bottom Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <DashboardCard title="Data Breach Attempts">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="w-6 h-6 text-red-500" />
                        <span className="text-3xl font-bold text-red-500">127</span>
                      </div>
                      <span className="text-xs text-gray-400">Last 30 Days</span>
                    </div>
                  </DashboardCard>

                  <DashboardCard title="Backup Status">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Database className="w-6 h-6 text-[#10b981]" />
                        <span className="text-3xl font-bold text-[#10b981]">99.5%</span>
                      </div>
                      <span className="text-xs text-gray-400">Data Backup Success</span>
                    </div>
                  </DashboardCard>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50" data-testid="benefits-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <span className="inline-block px-4 py-1.5 bg-[#1a3a52]/10 text-[#1a3a52] text-sm font-semibold rounded-full mb-4">
                Benefícios
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                Benefícios para a Liderança
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Transforme dados de segurança em inteligência estratégica
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 text-center group"
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#10b981]/10 flex items-center justify-center group-hover:bg-[#10b981] transition-colors">
                    <benefit.icon className="w-8 h-8 text-[#10b981] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1a3a52] mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-[#1a3a52] to-[#10b981] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)`
            }} />
          </div>
          
          <div className="container mx-auto px-4 text-center relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <Activity className="w-16 h-16 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Solicitar Demonstração do SmartAudit
              </h2>
              <p className="text-xl mb-10 text-gray-200">
                Veja na prática como o SmartAudit pode transformar sua gestão de riscos e compliance
              </p>
              <Link
                to="/contato"
                className="group inline-flex items-center gap-2 bg-white text-[#1a3a52] px-10 py-5 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-2xl hover:scale-105"
                data-testid="smartaudit-demo-btn"
              >
                Agendar Demonstração
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SmartAuditPage;
